import { DATE_FIELD_DATE_VALIDATION } from '../types'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import dayjs from 'dayjs'
import i18n from '../locales'
import { isSafari } from './isSafari'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekday from 'dayjs/plugin/weekday'

dayjs.extend(localizedFormat)
dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(weekday)
dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(relativeTime)
dayjs.extend(isSameOrAfter)

type DateInput = string | number | Date
export const formatDate = (date: DateInput, format = 'LLL') => {
  return dayjs(date).format(format)
}

export const isDateBefore = (date: DateInput) => {
  return dayjs().isBefore(date, 'day')
}

export const isValidDate = (date: DateInput) => {
  return dayjs(date).isValid()
}

export const convertToFormatForValidDate = (value: any) => {
  if (isValidDate(value)) return formatDate(value, 'LL')
  return value
}

export const validateDate = (
  availableDates = DATE_FIELD_DATE_VALIDATION.ANY as DATE_FIELD_DATE_VALIDATION,
  ts: number
) => {
  const now = dayjs()
  const date = dayjs(ts)
  if (availableDates === DATE_FIELD_DATE_VALIDATION.FUTURE_DATE) {
    return now.unix() > date.unix()
  }
  if (availableDates === DATE_FIELD_DATE_VALIDATION.PAST_DATE) {
    return now.unix() < date.unix()
  }
  if (availableDates === DATE_FIELD_DATE_VALIDATION.TODAYS_DATE) {
    return !now.isSame(date, 'day')
  }
  return false
}

/**
 *
 * @param date
 * @returns
 * FYI: https://medium.com/@papapapaya11/how-to-resolve-invalid-date-issue-in-chrome-or-safari-eebf7475262d#:~:text=During%20the%20implementation%20of%20a,Invalid%20date%20iso%208601%E2%80%9D%20specification.
 */
export const convertToUTC = (date: number | string) => {
  if (!date) {
    return null
  }
  const dateToSave = new Date(date)
  if (isSafari()) {
    return formatDate(dateToSave, 'DD-MMMM-YYYY')
  } else {
    return formatDate(dateToSave, 'DD/MMMM/YYYY')
  }
}

export const convertUtcToUNIX = (date: string) => {
  if (!date) return null
  if (isSafari() && date.includes('/')) {
    return new Date(date.replace(/\//g, '-'))
  }
  return new Date(date)
}

export const convertCustomValueToFormatForValidDate = (value: any) => {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/
  if (value && regex.test(value)) return formatDate(value, 'LL')
  return value
}

export const dayjsTZ = (
  date?: Date | string | number,
  format?: string,
  timezone?: string
) => {
  if (timezone) {
    if (date && format) {
      return dayjs.tz(dayjs(date).format(format), timezone)
    }
    return dayjs.tz(date, timezone)
  }
  return dayjs(date, format)
}

export const formatDateWithTZ = (
  date: string,
  format = 'MM/DD/YYYY',
  timezone?: string
) => {
  if (!date) return ''
  if (timezone) {
    return dayjs.tz(date, timezone).format(format)
  }
  return dayjs(date).format(format)
}

export const getCurrentDate = (format = 'YYYY-MM-DD', timezone?: string) => {
  if (timezone) {
    return dayjs().tz(timezone).format(format)
  }
  return dayjs().format(format)
}

export const getFormatDate = (
  date: string,
  format = 'YYYY-MM-DD',
  timezone?: string
) => {
  if (timezone) {
    return dayjs(date).tz(timezone).format(format)
  }
  return dayjs(date).format(format)
}

export const parseDateTimeInUTC = (dt: any) => {
  const timezoneOffset = dayjs().utcOffset()
  return dayjs.utc(dt).subtract(timezoneOffset, 'minutes')
}

export const getYearMonthDate = (date: Date | string) => {
  const parsedDate = dayjs(date, 'YYYY-MM-DD')
  return [parsedDate.year(), parsedDate.month(), parsedDate.date()]
}

export const getHourMinSec = (time: Date | string) => {
  const parsedTime = dayjs(time, 'HH:mm:ss')
  return [parsedTime.hour(), parsedTime.minute(), parsedTime.second()]
}

export const formatDayAbbreviation = (dayOfWeek: number) => {
  const formattedDay = dayjs().weekday(dayOfWeek).format('dd').toUpperCase()
  return formattedDay
}

export const formatMonthNumber = (monthOfYear: number) => {
  const formattedMonth = dayjs().month(monthOfYear).format('M')
  return formattedMonth
}

export const formatDateSubtractDays = (
  firstOccurrence: Date | string,
  daysBefore: number,
  format = 'MMMM D, YYYY',
  timezone?: string
) => {
  if (timezone) {
    return dayjs(firstOccurrence)
      .subtract(Number(daysBefore), 'days')
      .tz(timezone)
      .format(format)
  }
  return dayjs(firstOccurrence)
    .utc(true)
    .subtract(Number(daysBefore), 'days')
    .format(format)
}

export const formatLastViewedTime = (date: string, timezoneString = 'UTC') => {
  const now = dayjs().tz(timezoneString)
  const viewedTime = dayjs(date).tz(timezoneString)
  const yearsDifference = now.diff(viewedTime, 'year')
  const monthsDifference = now.diff(viewedTime, 'month')
  const weeksDifference = now.diff(viewedTime, 'week')
  const daysDifference = now.diff(viewedTime, 'day')
  const hoursDifference = now.diff(viewedTime, 'hour')
  const minutesDifference = now.diff(viewedTime, 'minute')
  if (yearsDifference >= 1) {
    return `${yearsDifference} ${i18n.global.t(
      yearsDifference > 1
        ? 'schedulingOptions.timeAgo.years'
        : 'schedulingOptions.timeAgo.year'
    )}`
  } else if (monthsDifference >= 1) {
    return `${monthsDifference} ${i18n.global.t(
      monthsDifference > 1
        ? 'schedulingOptions.timeAgo.months'
        : 'schedulingOptions.timeAgo.month'
    )}`
  } else if (weeksDifference >= 1) {
    return `${weeksDifference} ${i18n.global.t(
      weeksDifference > 1
        ? 'schedulingOptions.timeAgo.weeks'
        : 'schedulingOptions.timeAgo.week'
    )}`
  } else if (daysDifference >= 1) {
    return `${daysDifference} ${i18n.global.t(
      daysDifference > 1
        ? 'schedulingOptions.timeAgo.days'
        : 'schedulingOptions.timeAgo.day'
    )}`
  } else if (hoursDifference >= 1) {
    return `${hoursDifference} ${i18n.global.t(
      hoursDifference > 1
        ? 'schedulingOptions.timeAgo.hours'
        : 'schedulingOptions.timeAgo.hour'
    )}`
  } else {
    return `${minutesDifference} ${i18n.global.t(
      minutesDifference > 1
        ? 'schedulingOptions.timeAgo.minutes'
        : 'schedulingOptions.timeAgo.minute'
    )}`
  }
}

export const addDaysToDate = (dateStr: string | Date, daysToAdd: number) => {
  return dayjs(dateStr, 'YYYY-MM-DD').add(daysToAdd, 'day').format('YYYY-MM-DD')
}

export const addWeeksToDate = (dateStr: string | Date, weeksToAdd: number) => {
  return dayjs(dateStr, 'YYYY-MM-DD')
    .add(weeksToAdd, 'week')
    .format('YYYY-MM-DD')
}

export const addMonthsToDate = (
  dateStr: string | Date,
  monthsToAdd: number
) => {
  return dayjs(dateStr, 'YYYY-MM-DD')
    .add(monthsToAdd, 'month')
    .format('YYYY-MM-DD')
}

export const addYearsToDate = (dateStr: string | Date, yearsToAdd: number) => {
  return dayjs(dateStr, 'YYYY-MM-DD')
    .add(yearsToAdd, 'year')
    .format('YYYY-MM-DD')
}

/**
 * Checks if date1 is strictly after date2.
 *
 * @param date1 - The first date to compare.
 * @param date2 - The second date to compare against.
 * @param format - The date format to use (default is 'YYYY-MM-DD').
 * @param isUtc - Whether to treat the dates as UTC (default is false).
 * @returns True if date1 is strictly after date2; otherwise, false.
 */
export const isDateOnlyAfter = (
  date1: string | Date | dayjs.Dayjs,
  date2: string | Date | dayjs.Dayjs,
  format = 'YYYY-MM-DD',
  isUtc = false
): boolean => {
  const formattedDate1 = isUtc ? dayjs.utc(date1, format) : dayjs(date1, format)
  const formattedDate2 = isUtc ? dayjs.utc(date2, format) : dayjs(date2, format)
  return (
    formattedDate1.isAfter(formattedDate2) &&
    !formattedDate1.isSame(formattedDate2)
  )
}

/**
 * Wrapper function to check if a date is in the past (on a day-level comparison in UTC).
 *
 * @param date - The date to check.
 * @returns True if the given date is in the past, otherwise false.
 */
export const isDateInPastUTC = (date: string | Date | dayjs.Dayjs): boolean => {
  return dayjs.utc().isAfter(dayjs.utc(date), 'day')
}

/**
 * Adds days to current date/time preserving hour/minute precision
 * @param daysToAdd Number of days to add
 * @param timezone Optional timezone to use
 * @returns ISO string with timezone-aware date and time
 */
export const addDaysToDateTime = (
  daysToAdd: number,
  timezone?: string
): string => {
  if (timezone) {
    return dayjs().tz(timezone).add(daysToAdd, 'day').format()
  }
  return dayjs().add(daysToAdd, 'day').format()
}
